/**
 * Chip del estado del cierre de tarea en el ERP (POST cumplir-tarea) + modal
 * con el log de request/response leído del audit_log.
 *
 *   cumplida → verde · error → rojo (falló el informe al ERP) · null → gris.
 *
 * El "(ver log)" abre un modal que trae las entries `cumplir_tarea.*` de esa
 * tarea (por `erpId`) y muestra el request (iniciada) y el response
 * (success/error) tal como quedaron auditados.
 */

import { useState } from 'react';
import { Loader2, FileText } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useCumplirTareaLog } from '@/api/queries';
import { cn } from '@/lib/utils';
import { fmtDateTime } from '@/lib/format';
import type { CumplirTareaEstado } from '@/api/contracts/comprobantes';

interface Props {
  estado: CumplirTareaEstado | null | undefined;
  fecha?: string | null;
  erpId: string | null | undefined;
  /** Oculta el botón "(ver log)" — ej. cuando no hay erpId. */
  ocultarLog?: boolean;
}

const LABEL: Record<CumplirTareaEstado, string> = {
  cumplida: 'Tarea ERP cumplida',
  error: 'Error al informar al ERP',
};

const BADGE: Record<CumplirTareaEstado, string> = {
  cumplida: 'badge-green',
  error: 'badge-red',
};

export function CumplirTareaChip({ estado, fecha, erpId, ocultarLog = false }: Props) {
  const [logOpen, setLogOpen] = useState(false);

  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      {estado ? (
        <span className={cn('badge', BADGE[estado])} title={fecha ? fmtDateTime(fecha) : undefined}>
          {LABEL[estado]}
        </span>
      ) : (
        <span className="badge badge-gray">Cierre ERP pendiente</span>
      )}
      {estado === 'cumplida' && fecha && (
        <span className="text-[11px] text-gray-500">{fmtDateTime(fecha)}</span>
      )}
      {!ocultarLog && erpId && (
        <button
          type="button"
          onClick={() => setLogOpen(true)}
          className="text-[10px] text-gray-500 underline-offset-2 transition hover:text-everex-azul hover:underline"
          title="Ver request y response del cierre de tarea (audit trail)"
        >
          (ver log)
        </button>
      )}
      {erpId && (
        <CumplirTareaLogDialog erpId={erpId} open={logOpen} onOpenChange={setLogOpen} />
      )}
    </span>
  );
}

function CumplirTareaLogDialog({
  erpId,
  open,
  onOpenChange,
}: {
  erpId: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { data: entries, isLoading, error } = useCumplirTareaLog(erpId, open);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Cierre de tarea en el ERP
          </DialogTitle>
          <DialogDescription className="text-xs">
            Request y response del <code className="font-mono">cumplir-tarea</code> (tarea ERP #{erpId}),
            tal como quedaron en el audit trail.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8 text-gray-600">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            <span className="text-xs">Cargando log...</span>
          </div>
        ) : error ? (
          <Alert variant="destructive">
            <AlertDescription className="text-xs">
              {error instanceof Error ? error.message : String(error)}
            </AlertDescription>
          </Alert>
        ) : !entries || entries.length === 0 ? (
          <Alert>
            <AlertDescription className="text-xs">
              No hay registros de cierre de tarea para esta referencia todavía.
            </AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-3">
            {entries.map((e) => {
              const esError = e.action.endsWith('.error');
              const esIniciada = e.action.endsWith('.iniciada');
              return (
                <div
                  key={e.id}
                  className={cn(
                    'rounded border p-3',
                    esError
                      ? 'border-red-200 bg-red-50'
                      : esIniciada
                        ? 'border-blue-200 bg-blue-50'
                        : 'border-green-200 bg-green-50',
                  )}
                >
                  {/* Fecha/hora arriba de todo para saber cuándo fue cada intento. */}
                  <div className="mb-1.5 flex flex-wrap items-center gap-2">
                    <span className="font-mono text-[11px] font-semibold text-gray-700">
                      {e.createdAt ? fmtDateTime(e.createdAt) : 'sin fecha'}
                    </span>
                    <span
                      className={cn(
                        'badge',
                        esError ? 'badge-red' : esIniciada ? 'badge-blue' : 'badge-green',
                      )}
                    >
                      {esIniciada ? 'Request' : esError ? 'Error / Response' : 'Response OK'}
                    </span>
                  </div>
                  <pre className="overflow-x-auto rounded bg-white/70 p-2 font-mono text-[10px] leading-tight text-gray-800">
                    {JSON.stringify(e.metadata ?? {}, null, 2)}
                  </pre>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
